import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, Tag, AlertCircle, CheckSquare, Square, Plus, Trash2, Link as LinkIcon, CheckCircle2, AlertTriangle, FolderOpen } from 'lucide-react';
import Button from '../ui/Button';
import IconButton from '../ui/IconButton';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import { Task, Project, supabase, WorkHoursConfig, TaskBlock, CalendarEvent } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Drawer from '../ui/Drawer';
import DatePicker from '../ui/DatePicker';
import SearchableSelect from '../ui/SearchableSelect';
import { CommentsSection } from '../shared/CommentsSection';
import TaskTimer from './TaskTimer';
import { checkTaskFeasibility, FeasibilityResult } from '../../lib/workloadCalculator';
import { TaskScheduler } from '../../lib/taskScheduler';

interface ChecklistItem {
  id: string;
  content: string;
  is_completed: boolean;
  position: number;
}

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task?: Task | null;
  projectId?: string;
  projectName?: string;
  onSave: () => void;
}

export default function TaskModal({ isOpen, onClose, task, projectId, projectName, onSave }: TaskModalProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [availableTasks, setAvailableTasks] = useState<Task[]>([]);
  const [selectedDependency, setSelectedDependency] = useState<string>('');

  const [feasibility, setFeasibility] = useState<FeasibilityResult | null>(null);
  const [feasibilityLoading, setFeasibilityLoading] = useState(false);

  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    tipo: 'edicion' as Task['tipo'],
    estado: 'por_empezar' as Task['estado'],
    prioridad: 'normal' as Task['prioridad'],
    horas_estimadas: '',
    horas_ejecutadas: '',
    fecha_inicio: '',
    fecha_fin: '',
    depends_on_task_id: null as string | null,
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const tag = (e.target as HTMLElement).tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      if (!projectId) {
        fetchProjects();
      }

      setSelectedProjectId(projectId || '');

      if (task) {
        setFormData({
          nombre: task.nombre,
          descripcion: task.descripcion || '',
          tipo: task.tipo,
          estado: task.estado,
          prioridad: task.prioridad,
          horas_estimadas: String(task.horas_estimadas),
          horas_ejecutadas: String(task.horas_ejecutadas),
          fecha_inicio: task.fecha_inicio || '',
          fecha_fin: task.fecha_fin || '',
          depends_on_task_id: (task as any).depends_on_task_id || null,
        });
        setSelectedDependency((task as any).depends_on_task_id || '');
        fetchChecklistItems(task.id);
        fetchAvailableTasks(task.id, task.project_id);
      } else {
        setFormData({
          nombre: '',
          descripcion: '',
          tipo: 'edicion',
          estado: 'por_empezar',
          prioridad: 'normal',
          horas_estimadas: '',
          horas_ejecutadas: '',
          fecha_inicio: '',
          fecha_fin: '',
          depends_on_task_id: null,
        });
        setSelectedDependency('');
        setChecklistItems([]);
        if (projectId) {
          fetchAvailableTasks(null, projectId);
        }
      }
    }
  }, [isOpen, task, projectId]);

  const runFeasibilityCheck = useCallback(async (fechaInicio: string, fechaFin: string, horasEstimadas: number) => {
    if (!user || !fechaInicio || !fechaFin || horasEstimadas <= 0) {
      setFeasibility(null);
      return;
    }

    setFeasibilityLoading(true);
    try {
      const config = await TaskScheduler.getOrCreateConfig(user.id);

      const { data: blocks } = await supabase
        .from('apro_task_blocks')
        .select('*')
        .eq('user_id', user.id);

      const { data: events } = await supabase
        .from('apro_calendar_events')
        .select('*')
        .eq('user_id', user.id)
        .eq('event_type', 'external');

      const result = checkTaskFeasibility(
        fechaInicio,
        fechaFin,
        horasEstimadas,
        config,
        (blocks || []) as TaskBlock[],
        (events || []) as CalendarEvent[],
        task?.id
      );

      setFeasibility(result);
    } catch (error) {
      console.error('Error checking feasibility:', error);
      setFeasibility(null);
    } finally {
      setFeasibilityLoading(false);
    }
  }, [user, task?.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const rawRemaining = (parseFloat(formData.horas_estimadas) || 0) - (parseFloat(formData.horas_ejecutadas) || 0);
      const hoursToSchedule = rawRemaining > 0 ? rawRemaining : 0.5;
      runFeasibilityCheck(formData.fecha_inicio, formData.fecha_fin, hoursToSchedule);
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.fecha_inicio, formData.fecha_fin, formData.horas_estimadas, formData.horas_ejecutadas, runFeasibilityCheck]);

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('apro_projects')
        .select('id, nombre')
        .eq('created_by', user?.id || '')
        .eq('archived', false)
        .order('nombre');

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchChecklistItems = async (taskId: string) => {
    try {
      const { data, error } = await supabase
        .from('apro_task_checklist_items')
        .select('*')
        .eq('task_id', taskId)
        .order('position');

      if (error) throw error;
      setChecklistItems(data || []);
    } catch (error) {
      console.error('Error fetching checklist items:', error);
    }
  };

  const fetchAvailableTasks = async (currentTaskId: string | null, currentProjectId: string) => {
    try {
      let query = supabase
        .from('apro_tasks')
        .select('id, nombre')
        .eq('project_id', currentProjectId)
        .in('estado', ['por_empezar', 'en_marcha', 'en_revision', 'terminada'])
        .order('nombre');

      if (currentTaskId) {
        query = query.neq('id', currentTaskId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setAvailableTasks(data || []);
    } catch (error) {
      console.error('Error fetching available tasks:', error);
    }
  };

  const addChecklistItem = async () => {
    if (!task || !newChecklistItem.trim()) return;

    try {
      const position = checklistItems.length;
      const { data, error } = await supabase
        .from('apro_task_checklist_items')
        .insert({
          task_id: task.id,
          content: newChecklistItem.trim(),
          position,
          is_completed: false,
        })
        .select()
        .single();

      if (error) throw error;

      setChecklistItems([...checklistItems, data]);
      setNewChecklistItem('');
    } catch (error) {
      console.error('Error adding checklist item:', error);
      alert('Error al añadir el elemento');
    }
  };

  const toggleChecklistItem = async (itemId: string, isCompleted: boolean) => {
    try {
      const { error } = await supabase
        .from('apro_task_checklist_items')
        .update({ is_completed: !isCompleted })
        .eq('id', itemId);

      if (error) throw error;

      setChecklistItems(checklistItems.map(item =>
        item.id === itemId ? { ...item, is_completed: !isCompleted } : item
      ));
    } catch (error) {
      console.error('Error toggling checklist item:', error);
      alert('Error al actualizar el elemento');
    }
  };

  const deleteChecklistItem = async (itemId: string) => {
    try {
      const { error } = await supabase
        .from('apro_task_checklist_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      setChecklistItems(checklistItems.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error deleting checklist item:', error);
      alert('Error al eliminar el elemento');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        horas_estimadas: parseFloat(formData.horas_estimadas) || 0,
        horas_ejecutadas: parseFloat(formData.horas_ejecutadas) || 0,
      };

      if (task) {
        const { error } = await supabase
          .from('apro_tasks')
          .update({
            ...submitData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', task.id);

        if (error) throw error;
      } else {
        const targetProjectId = projectId || selectedProjectId;

        if (!targetProjectId) {
          alert('Por favor selecciona un proyecto');
          setLoading(false);
          return;
        }

        const { error } = await supabase
          .from('apro_tasks')
          .insert({
            ...submitData,
            project_id: targetProjectId,
            created_by: user?.id,
          });

        if (error) throw error;
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Error al guardar la tarea');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldUpdate = async (field: string, value: any) => {
    if (!task) return;

    try {
      const { error } = await supabase
        .from('apro_tasks')
        .update({ [field]: value, updated_at: new Date().toISOString() })
        .eq('id', task.id);

      if (error) throw error;
      onSave();
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Error al actualizar la tarea');
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={task ? 'Editar tarea' : 'Nueva tarea'}
      subtitle={projectName || undefined}
      width="xl"
    >
      <div className="p-4 md:p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="mb-8">
                <Input
                  type="text"
                  required
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  onBlur={() => task && handleFieldUpdate('nombre', formData.nombre)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="w-full"
                  placeholder="Nombre de la tarea"
                />
              </div>

              <div className="space-y-4">
                {!projectId && (
                  <div className="flex items-center gap-4 py-3 border-b border-gray-800">
                    <div className="flex items-center gap-2 text-gray-400 w-32">
                      <FolderOpen className="w-4 h-4" />
                      <span className="text-sm">Proyecto</span>
                    </div>
                    <SearchableSelect
                      value={selectedProjectId}
                      onChange={(v) => {
                        setSelectedProjectId(v);
                        setSelectedDependency('');
                        setFormData((prev) => ({ ...prev, depends_on_task_id: null }));
                        if (v) {
                          fetchAvailableTasks(task?.id || null, v);
                        } else {
                          setAvailableTasks([]);
                        }
                      }}
                      options={projects.map((p) => ({ value: p.id, label: p.nombre }))}
                      placeholder="Seleccionar proyecto"
                      searchPlaceholder="Buscar proyecto…"
                      className="flex-1"
                    />
                  </div>
                )}

                <div className="flex items-center gap-4 py-3 border-b border-gray-800">
                  <div className="flex items-center gap-2 text-gray-400 w-32">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">Estado</span>
                  </div>
                  <SearchableSelect
                    value={formData.estado}
                    onChange={(v) => {
                      const value = v as Task['estado'];
                      setFormData({ ...formData, estado: value });
                      if (task) handleFieldUpdate('estado', value);
                    }}
                    options={[
                      { value: 'por_empezar', label: 'Por empezar' },
                      { value: 'en_marcha', label: 'En marcha' },
                      { value: 'en_revision', label: 'En revisión' },
                      { value: 'terminada', label: 'Terminada' },
                    ]}
                    className="flex-1"
                  />
                </div>

                <div className="flex items-center gap-4 py-3 border-b border-gray-800">
                  <div className="flex items-center gap-2 text-gray-400 w-32">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">Prioridad</span>
                  </div>
                  <SearchableSelect
                    value={formData.prioridad}
                    onChange={(v) => {
                      const value = v as Task['prioridad'];
                      setFormData({ ...formData, prioridad: value });
                      if (task) handleFieldUpdate('prioridad', value);
                    }}
                    options={[
                      { value: 'baja', label: 'Baja' },
                      { value: 'normal', label: 'Normal' },
                      { value: 'alta', label: 'Alta' },
                    ]}
                    className="flex-1"
                  />
                </div>

                <div className="flex items-center gap-4 py-3 border-b border-gray-800">
                  <div className="flex items-center gap-2 text-gray-400 w-32">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">Fechas</span>
                  </div>
                  <div className="flex-1 flex items-center gap-2">
                    <DatePicker
                      value={formData.fecha_inicio}
                      onChange={(v) => {
                        setFormData({ ...formData, fecha_inicio: v });
                        if (task) handleFieldUpdate('fecha_inicio', v);
                      }}
                      placeholder="Inicio"
                      className="flex-1"
                    />
                    <span className="text-gray-500">→</span>
                    <DatePicker
                      value={formData.fecha_fin}
                      onChange={(v) => {
                        setFormData({ ...formData, fecha_fin: v });
                        if (task) handleFieldUpdate('fecha_fin', v);
                      }}
                      placeholder="Fin"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4 py-3 border-b border-gray-800">
                  <div className="flex items-center gap-2 text-gray-400 w-32">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Horas estimadas</span>
                  </div>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.horas_estimadas}
                    onChange={(e) => {
                      setFormData({ ...formData, horas_estimadas: e.target.value });
                    }}
                    onBlur={() => {
                      if (task) handleFieldUpdate('horas_estimadas', parseFloat(formData.horas_estimadas) || 0);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="flex-1"
                    placeholder="Horas estimadas"
                  />
                </div>

                <div className="flex items-center gap-4 py-3 border-b border-gray-800">
                  <div className="flex items-center gap-2 text-gray-400 w-32">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Horas ejecutadas</span>
                  </div>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.horas_ejecutadas}
                    onChange={(e) => {
                      setFormData({ ...formData, horas_ejecutadas: e.target.value });
                    }}
                    onBlur={() => {
                      if (task) handleFieldUpdate('horas_ejecutadas', parseFloat(formData.horas_ejecutadas) || 0);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="flex-1"
                    placeholder="Horas ejecutadas"
                  />
                </div>

                {/* Feasibility indicator */}
                {feasibility && (
                  <div className={`flex items-start gap-3 py-3 px-3 rounded-panel border ${
                    feasibility.status === 'viable'
                      ? 'bg-success-500/10 border-success-500/30'
                      : feasibility.status === 'tight'
                      ? 'bg-warning-500/10 border-warning-500/30'
                      : 'bg-danger-500/10 border-danger-500/30'
                  }`}>
                    {feasibility.status === 'viable' ? (
                      <CheckCircle2 className="w-4 h-4 text-success-400 mt-0.5 flex-shrink-0" />
                    ) : feasibility.status === 'tight' ? (
                      <AlertTriangle className="w-4 h-4 text-warning-400 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-danger-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        feasibility.status === 'viable' ? 'text-success-300' :
                        feasibility.status === 'tight' ? 'text-warning-300' : 'text-danger-300'
                      }`}>
                        {feasibility.message}
                      </p>
                      {feasibility.suggestedEndDate && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, fecha_fin: feasibility.suggestedEndDate! }));
                            if (task) handleFieldUpdate('fecha_fin', feasibility.suggestedEndDate!);
                          }}
                          className="text-xs text-brand-400 hover:text-brand-300 mt-1 underline px-0 h-auto"
                        >
                          Usar fecha sugerida: {feasibility.suggestedEndDate}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-4 py-3 border-b border-gray-800">
                  <div className="flex items-center gap-2 text-gray-400 w-32">
                    <Tag className="w-4 h-4" />
                    <span className="text-sm">Tipo</span>
                  </div>
                  <SearchableSelect
                    value={formData.tipo}
                    onChange={(v) => {
                      const value = v as Task['tipo'];
                      setFormData({ ...formData, tipo: value });
                      if (task) handleFieldUpdate('tipo', value);
                    }}
                    options={[
                      { value: 'rodaje', label: 'Rodaje' },
                      { value: 'edicion', label: 'Edición' },
                      { value: 'reunion', label: 'Reunión' },
                      { value: 'seguimiento', label: 'Seguimiento' },
                    ]}
                    className="flex-1"
                  />
                </div>

                {availableTasks.length > 0 && (
                  <div className="flex items-center gap-4 py-3 border-b border-gray-800">
                    <div className="flex items-center gap-2 text-gray-400 w-32">
                      <LinkIcon className="w-4 h-4" />
                      <span className="text-sm">Depende de</span>
                    </div>
                    <SearchableSelect
                      value={selectedDependency}
                      onChange={(v) => {
                        const value = v || null;
                        setSelectedDependency(v);
                        setFormData({ ...formData, depends_on_task_id: value });
                        if (task) handleFieldUpdate('depends_on_task_id', value);
                      }}
                      options={[
                        { value: '', label: 'Sin dependencia' },
                        ...availableTasks.map((t) => ({ value: t.id, label: t.nombre })),
                      ]}
                      placeholder="Sin dependencia"
                      searchPlaceholder="Buscar tarea…"
                      className="flex-1"
                    />
                  </div>
                )}

                {/* TaskTimer is intentionally NOT rendered here — it lives outside
                    the <form> below to avoid nested-form HTML issues that caused
                    the CommentModal to trigger TaskModal's form submission. */}
              </div>

              <div className="mt-8">
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Descripción
                </label>
                <Textarea
                  rows={4}
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  onBlur={() => task && handleFieldUpdate('descripcion', formData.descripcion)}
                  placeholder="Añade una descripción..."
                />
              </div>

              {task && (
                <div className="mt-8">
                  <label className="block text-sm font-medium text-gray-400 mb-3">
                    Checklist
                  </label>
                  <div className="space-y-2">
                    {checklistItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 group bg-gray-800 border border-gray-700 rounded-input px-3 py-2 hover:border-gray-600 transition-colors"
                      >
                        <IconButton
                          icon={item.is_completed ? CheckSquare : Square}
                          aria-label={item.is_completed ? 'Desmarcar elemento' : 'Marcar elemento'}
                          variant="ghost"
                          size="sm"
                          type="button"
                          onClick={() => toggleChecklistItem(item.id, item.is_completed)}
                          className="flex-shrink-0"
                        />
                        <span
                          className={`flex-1 text-sm ${
                            item.is_completed
                              ? 'text-gray-500 line-through'
                              : 'text-gray-200'
                          }`}
                        >
                          {item.content}
                        </span>
                        <IconButton
                          icon={Trash2}
                          aria-label="Eliminar elemento"
                          variant="danger"
                          size="sm"
                          type="button"
                          onClick={() => deleteChecklistItem(item.id)}
                          className="opacity-0 group-hover:opacity-100"
                        />
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={newChecklistItem}
                        onChange={(e) => setNewChecklistItem(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addChecklistItem();
                          }
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="flex-1"
                        placeholder="Añadir elemento a la checklist..."
                      />
                      <IconButton
                        icon={Plus}
                        aria-label="Añadir elemento"
                        variant="secondary"
                        type="button"
                        onClick={addChecklistItem}
                      />
                    </div>
                  </div>
                </div>
              )}

              {!task && (
                <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-800">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={onClose}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={loading}
                  >
                    Crear tarea
                  </Button>
                </div>
              )}
            </form>

            {/* Timer lives OUTSIDE the <form> to prevent nested-form issues.
                When TaskTimer shows CommentModal, its submit button must never
                be associated with the form above. */}
            {task && (
              <div className="flex items-center gap-4 py-3 border-b border-gray-800 px-0">
                <div className="flex items-center gap-2 text-gray-400 w-32">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Timer</span>
                </div>
                <div className="flex-1">
                  <TaskTimer task={task} onUpdate={onSave} />
                </div>
              </div>
            )}
          </div>
        {task && (
          <div className="border-t border-gray-800">
            <CommentsSection taskId={task.id} className="h-full" />
          </div>
        )}
      </Drawer>
  );
}
